import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveBackPath } from '../utils/routeParents';

const readUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
};

/**
 * iOS-style edge swipe-back gesture.
 *
 * Listens globally for one-finger touches that begin near the left edge of the
 * viewport and travel rightward. On release, if the gesture exceeded a
 * commit threshold (40 % of viewport width OR ≥ 60 px with velocity
 * > 0.55 px/ms) we call `navigate(-1)`. The existing AnimatePresence route
 * fade handles the visual handoff.
 *
 * NOTE: we deliberately do NOT translate the page wrapper while the user
 * drags. Applying a CSS transform on a `position: fixed` element's ancestor
 * would establish a new containing block and break our fixed bottom nav
 * (it would be anchored to the page rather than the viewport).
 */
const EDGE_PX = 24;
const COMMIT_RATIO = 0.4;
const COMMIT_VELOCITY = 0.55; // px/ms
const VERTICAL_TOLERANCE = 12;

export const useSwipeBack = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  navigateRef.current = navigate;
  locationRef.current = location;

  useEffect(() => {
    let tracking = false;
    let startX = 0;
    let startY = 0;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0;

    const onTouchStart = (e) => {
      if (!e.touches || e.touches.length !== 1) return;
      const t = e.touches[0];
      if (t.clientX > EDGE_PX) return;
      tracking = true;
      startX = t.clientX;
      startY = t.clientY;
      lastX = startX;
      lastTime = performance.now();
      velocity = 0;
    };

    const onTouchMove = (e) => {
      if (!tracking || !e.touches || e.touches.length !== 1) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);

      if (dy > Math.abs(dx) && dy > VERTICAL_TOLERANCE) {
        tracking = false;
        return;
      }
      if (dx < 0) return;

      const now = performance.now();
      const dt = Math.max(1, now - lastTime);
      velocity = (t.clientX - lastX) / dt;
      lastX = t.clientX;
      lastTime = now;
    };

    const onTouchEnd = () => {
      if (!tracking) return;
      tracking = false;
      const dx = Math.max(0, lastX - startX);
      const width = window.innerWidth;
      if (dx > width * COMMIT_RATIO || (dx > 60 && velocity > COMMIT_VELOCITY)) {
        const target = resolveBackPath(locationRef.current.pathname, readUser());
        navigateRef.current(target);
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []);
};

export default useSwipeBack;
